import React, { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { saveRole } from "../../actions/admin";
import { accessLabel, accessHint, accessGroups } from "./roleLabels";
import {
  fieldClass,
  labelClass,
  primaryButton,
  secondaryButton,
} from "../template/templateLabels";

// As the backend's POST /api/admin/roles allows
const NAME_MAX = 40;

const checkboxClass =
  "text-purple-600 rounded focus:ring-purple-200 focus:ring-opacity-50 focus:ring";

// Adds a role (no role) or edits one: its name and what it opens. The backend
// has the keys a role can be given, and refuses full and one's own role, which
// the Users page does not offer to edit.
const RoleForm = ({ role, keys, onClose, saveRole }) => {
  const isNew = !role;
  const [name, setName] = useState(isNew ? "" : role.name);
  // A key the backend no longer has is dropped on save
  const [access, setAccess] = useState(
    isNew ? [] : role.access.filter((key) => keys.includes(key))
  );
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const trimmed = name.trim();
  const nameValid = trimmed !== "" && trimmed.length <= NAME_MAX;
  const canSubmit = !submitting && nameValid && access.length > 0;

  // An action is of no use without its section, so it goes with it
  const toggle = (key) =>
    setAccess((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key && !item.startsWith(key + "."))
        : [...current, key]
    );

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErrors([]);
    const result = await saveRole(isNew ? null : role.role, { name: trimmed, access });
    if (result.role) {
      onClose();
    } else {
      setErrors(result.errors.map((error) => error.msg));
      setSubmitting(false);
    }
  };

  const close = () => {
    if (!submitting) onClose();
  };

  const option = (key, disabled) => (
    <label
      className={
        "flex items-start py-1.5 " +
        (disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer")
      }
    >
      <input
        type="checkbox"
        checked={access.includes(key)}
        onChange={() => toggle(key)}
        disabled={disabled}
        className={"mt-0.5 " + checkboxClass}
      />
      <span className="ml-3">
        <span className="block text-sm text-gray-700 dark:text-gray-200">
          {accessLabel(key)}
        </span>
        {accessHint(key) && (
          <span className="block text-xs text-gray-500 dark:text-gray-400">
            {accessHint(key)}
          </span>
        )}
      </span>
    </label>
  );

  return (
    // Only a press on the backdrop itself closes the sheet
    <div
      className="fixed inset-0 z-30 flex items-end pt-12 bg-black bg-opacity-50 sm:items-center sm:justify-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <form
        onSubmit={submit}
        className="flex flex-col w-full max-h-full overflow-hidden bg-white rounded-t-lg dark:bg-gray-800 sm:rounded-lg sm:max-w-lg"
      >
        <header className="flex items-start justify-between flex-shrink-0 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
          <div className="min-w-0">
            <p className="text-lg font-semibold text-gray-700 truncate dark:text-gray-300">
              {isNew ? "Шинэ эрх" : "Эрх засах"}
            </p>
            {!isNew && (
              <p className="text-sm text-gray-600 truncate dark:text-gray-400">
                {role.name}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="inline-flex items-center justify-center w-6 h-6 ml-3 text-gray-400 transition-colors duration-150 rounded dark:hover:text-gray-200 hover:text-gray-700"
            aria-label="close"
          >
            <i className="fas fa-times" />
          </button>
        </header>

        <div className="min-h-0 px-6 py-4 space-y-4 overflow-y-auto">
          {errors.length > 0 && (
            <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md dark:bg-red-900 dark:text-red-100">
              {errors.map((message, i) => (
                <p key={i}>{message}</p>
              ))}
            </div>
          )}

          <label className="block">
            <span className={labelClass}>Нэр</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
              maxLength={NAME_MAX}
              placeholder="Жишээ нь: Нягтлан"
              autoFocus
              className={fieldClass}
            />
          </label>

          <fieldset>
            <legend className={labelClass}>Нээх хэсгүүд</legend>
            <div className="space-y-3">
              {accessGroups(keys).map((group) => (
                <div
                  key={group.title}
                  className="p-3 border border-gray-200 rounded-lg dark:border-gray-600"
                >
                  <p className="mb-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
                    {group.title}
                  </p>
                  {group.items.map((item) => (
                    <div key={item.key}>
                      {option(item.key, submitting)}
                      {item.actions.length > 0 && (
                        <div className="pl-7">
                          {/* One left on without its section can still be taken off */}
                          {item.actions.map((key) => (
                            <div key={key}>
                              {option(
                                key,
                                submitting ||
                                  (!access.includes(item.key) && !access.includes(key))
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {access.length === 0 && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Дор хаяж нэг хэсэг сонгоно уу.
              </p>
            )}
          </fieldset>
        </div>

        <footer className="flex justify-end flex-shrink-0 px-6 py-3 space-x-3 border-t border-gray-100 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className={secondaryButton}
          >
            Болих
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={
              primaryButton + (canSubmit ? "" : " opacity-50 cursor-not-allowed")
            }
          >
            {submitting ? "Хадгалж байна..." : isNew ? "Нэмэх" : "Хадгалах"}
          </button>
        </footer>
      </form>
    </div>
  );
};

RoleForm.propTypes = {
  role: PropTypes.object,
  keys: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  saveRole: PropTypes.func.isRequired,
};

export default connect(null, { saveRole })(RoleForm);
